/**
 * What you are holding, right now, in words.
 *
 * A player who has been at this a while reads two hole cards against a board
 * without thinking about it. Somebody new cannot, and the game gives them no
 * way to learn: the only time a hand is ever named is at showdown, after it
 * stops mattering. So the table says it as it happens — pair on the flop, two
 * pair on the turn — and by the hundredth hand they no longer need it.
 *
 * It runs here, in the browser, on cards this player already holds. Asking the
 * server would mean the server sending a hand's value over the wire, and the
 * whole point of `@pf/engine` being a pure function with no I/O in it is that
 * both ends can run it and get the same answer. Nothing about anyone else's
 * cards is involved, so there is nothing here to leak.
 */

import { CATEGORY_NAMES, cardsFromString, categoryOf, evaluate } from '@pf/engine';
import type { CardCode } from '@pf/protocol';

export interface HandStrength {
  /** The engine's category name, e.g. `two-pair`. */
  readonly category: string;
  /** True the first time this category is reached on this board. */
  readonly improved: boolean;
}

/**
 * Name the best five-card hand available from these cards.
 *
 * Fewer than five cards has no answer — pre-flop you hold two, and calling
 * that "high card" would be worse than saying nothing, because it reads as a
 * verdict on a hand nobody has finished dealing.
 */
export function strengthOf(hole: readonly CardCode[], board: readonly CardCode[]): string | null {
  if (hole.length === 0 || hole.length + board.length < 5) return null;
  const cards = cardsFromString([...hole, ...board].join(' '));
  return CATEGORY_NAMES[categoryOf(evaluate(cards, cards.length))] ?? null;
}
